import { DocumentName } from "@components/documents/DocumentName";
import { useDownloadFinancialDocumentFile } from "@components/documents/FinancialDocumentDetails/useDownloadFinancialDocumentFile";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { ReceiptUploadDropzone } from "@components/transactions/ReceiptUpload/ReceiptUploadDropzone";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { toBase64 } from "@helpers/file";
import {
    getTransactionDetails,
    uploadTransactionReceipt,
} from "@lib/transactions";
import { useDetachTransactionMatchMutation } from "@mutations/transaction";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    memo,
    useCallback,
    useContext,
    useMemo,
    useRef,
    type ReactNode,
} from "react";
import { useTransactionViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useRemoveFinancialDocumentMutation } from "@api/financialDocument.api";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import styles from "./ReceiptsCell.module.scss";

enum ReceiptAction {
    DOWNLOAD = "download",
    DELETE = "delete",
    REMOVE = "remove",
}

const RECEIPT_OPTIONS = [
    {
        label: "Download receipt",
        value: ReceiptAction.DOWNLOAD,
    },
    {
        label: "Remove from transaction",
        value: ReceiptAction.REMOVE,
    },
    {
        label: "Delete receipt",
        value: ReceiptAction.DELETE,
    },
];

export const ReceiptsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, any>) => {
        const { transactionMatches } = row.original;
        const { error: errorToast } = useToaster();
        const { options: tableOptions } = useTransactionViewTableMeta(table);
        const isReadonly = tableOptions?.readonly ?? false;

        const receipts = useMemo(
            () =>
                transactionMatches?.filter(
                    (match) => match.financialDocument,
                ) ?? [],
            [transactionMatches],
        );

        const firstReceipt = receipts[0];
        const fileInputRef = useRef<HTMLInputElement>(null);
        const { upsertTransaction } = useContext(
            PaginatedTransactionsActionsContext,
        );

        const financialDocument = useMemo(
            () => firstReceipt?.financialDocument ?? null,
            [firstReceipt?.financialDocument],
        );

        const { download } =
            useDownloadFinancialDocumentFile(financialDocument);
        const detachMutation = useDetachTransactionMatchMutation(
            row.original.id,
            firstReceipt?.id,
        );
        const removeDocumentMutation = useRemoveFinancialDocumentMutation();

        const transactionId = row.original.id;

        const originalTransaction = row.original;

        const firstReceiptFinancialDocument = firstReceipt?.financialDocument;

        const refetchTransaction = useCallback(async () => {
            const updatedTransaction =
                await getTransactionDetails(transactionId);
            upsertTransaction(updatedTransaction.transaction);
        }, [transactionId, upsertTransaction]);

        const handleAction = useCallback(
            async (action: ReceiptAction) => {
                try {
                    switch (action) {
                        case ReceiptAction.DOWNLOAD:
                            if (firstReceiptFinancialDocument) {
                                await download();
                            } else {
                                throw new Error("No receipt found");
                            }
                            break;
                        case ReceiptAction.DELETE:
                            if (firstReceiptFinancialDocument) {
                                await removeDocumentMutation.mutateAsync({
                                    financialDocumentId:
                                        firstReceiptFinancialDocument.id,
                                    confirmTransactionsRemoval: true,
                                });
                                await refetchTransaction();
                            }
                            break;
                        case ReceiptAction.REMOVE:
                            if (firstReceipt) {
                                await detachMutation.mutateAsync();
                                await refetchTransaction();
                            }
                            break;
                    }
                } catch {
                    errorToast(
                        `Failed to ${action} receipt. Please try again.`,
                    );
                    upsertTransaction(originalTransaction);
                }
            },
            [
                firstReceiptFinancialDocument,
                firstReceipt,
                download,
                removeDocumentMutation,
                refetchTransaction,
                detachMutation,
                upsertTransaction,
                originalTransaction,
                errorToast,
            ],
        );

        const handleFileUpload = useCallback(
            async (file: File) => {
                if (isReadonly) {
                    return;
                }
                try {
                    const receipt = await toBase64(file);
                    await uploadTransactionReceipt(transactionId, {
                        receipt,
                        contentType: file.type,
                        name: file.name,
                    });

                    await invalidateTransactionDetailsQueries();
                    const updatedTransaction =
                        await getTransactionDetails(transactionId);

                    upsertTransaction(updatedTransaction.transaction);
                } catch {
                    errorToast("Failed to upload receipt. Please try again.");
                    upsertTransaction(originalTransaction);
                }
            },
            [
                isReadonly,
                transactionId,
                originalTransaction,
                upsertTransaction,
                errorToast,
            ],
        );

        const handleInputFileUpload = useCallback(
            async (event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (!file) {
                    return;
                }

                await handleFileUpload(file);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            },
            [handleFileUpload],
        );

        const handleUploadClick = useCallback(() => {
            if (isReadonly) {
                return;
            }
            fileInputRef.current?.click();
        }, [isReadonly]);

        const handleReceiptAction = useCallback(
            (action: ReceiptAction) => {
                void handleAction(action);
            },
            [handleAction],
        );

        let receiptContent: ReactNode;
        if (firstReceipt) {
            receiptContent = (
                <Picker.Select disabled={isReadonly}>
                    <Picker.CellContent
                        readOnly={isReadonly}
                        value={
                            <DocumentName
                                fileName={
                                    firstReceipt.financialDocument?.fileName ??
                                    ""
                                }
                                icon
                                className={styles.documentName}
                                textClassName={styles.documentNameText}
                                documentId={firstReceipt.financialDocument?.id}
                            />
                        }
                        aria-label={firstReceipt.financialDocument?.fileName}
                    />
                    <Picker.Popup>
                        <Picker.List>
                            {RECEIPT_OPTIONS.map((option) => (
                                <Picker.Item
                                    key={option.value}
                                    onSelect={() =>
                                        handleReceiptAction(option.value)
                                    }
                                >
                                    {option.label}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            );
        } else if (isReadonly) {
            receiptContent = (
                <EditableCellContent
                    className={styles.editableCellContent}
                    isEmpty
                    readonly
                />
            );
        } else {
            receiptContent = (
                <ReceiptUploadDropzone
                    onAddReceipt={handleFileUpload}
                    containerClassName={styles.dropzoneContainer}
                >
                    <EditableCellContent
                        className={styles.editableCellContent}
                        onClick={handleUploadClick}
                        isEmpty
                    />
                </ReceiptUploadDropzone>
            );
        }

        return (
            <ViewTableCell captureClicks>
                {receiptContent}

                {!isReadonly && (
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={handleInputFileUpload}
                    />
                )}
            </ViewTableCell>
        );
    },
);
ReceiptsCell.displayName = "ReceiptsCell";
